import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui';

import { formatCurrency, minorToMajor } from '@/lib';

interface ResultParams {
  status: 'success' | 'canceled';
  upsellId: string;
  amount?: string;
  currency?: string;
}

function UpsellResult() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('upsells');
  const location = useLocation();
  const inPortal = location.pathname.startsWith('/portal/');
  const homePath = inPortal ? '/portal/home' : '/';
  const addOnsPath = inPortal ? '/portal/home/add-ons' : '/add-ons';
  const [params, setParams] = useState<ResultParams | null>(null);

  useEffect(() => {
    const status = searchParams.get('status') as 'success' | 'canceled';
    const upsellId = searchParams.get('upsellId');
    const amount = searchParams.get('amount') || undefined;
    const currency = searchParams.get('currency') || undefined;

    if (status && upsellId) {
      setParams({ status, upsellId, amount, currency });
    }
  }, [searchParams]);

  if (!params) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] p-8">
        <div className="text-center space-y-6">
          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('Invalid Link')}</h2>
            <p className="text-gray-600">{t('This result link appears to be invalid or incomplete.')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (params.status === 'success') {
    return (
      <div className="flex items-center justify-center min-h-[50vh] p-8">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">{t('Purchase Successful')}</h2>
            <p className="text-gray-600">{t('Your add-on has been confirmed.')}</p>
          </div>

          {params.amount && params.currency && Number(params.amount) > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="text-center">
                <span className="text-lg font-medium text-gray-900">
                  {formatCurrency(minorToMajor(Number(params.amount), params.currency), params.currency)}
                </span>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm text-blue-800 text-left">
                <p>{t('Your host has been notified and will fulfill your request.')}</p>
              </div>
            </div>
          </div>

          <Button
            onClick={() => navigate(addOnsPath)}
            className="w-full rounded-full h-14 text-base font-medium"
            variant="primary"
            size="lg"
          >
            {t('Browse more add-ons')}
          </Button>
        </div>
      </div>
    );
  }

  // Canceled
  return (
    <div className="flex items-center justify-center min-h-[50vh] p-8">
      <div className="text-center space-y-6 max-w-md">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">{t('Purchase Canceled')}</h2>
          <p className="text-gray-600">
            {t('No worries. Your payment was not completed and you have not been charged.')}
          </p>
        </div>

        <div className="space-y-3">
          <Button
            onClick={() => navigate(addOnsPath)}
            className="w-full rounded-full h-14 text-base font-medium"
            variant="primary"
            size="lg"
          >
            {t('Try Again')}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(homePath)}
            className="w-full rounded-full h-14 text-base font-medium"
            size="lg"
          >
            {t('Go Back')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default UpsellResult;
